namespace Solovis.Reports.Parameters {

  export interface DimensionRefValue {
    dimensionType: "tag" | "manager";
    tag?: any;
    manager?: any;
  }

  /**
   * A single chain entry for a Tag Benchmark Association: either a tag-set dimension (delegates to
   * TagParameterControl) or a Manager dimension (delegates to ManagerParameterControl), switchable via a
   * per-row type selector. Used as the per-item control for the "values" list parameter in
   * TagBenchmarkAssociationsEditorControl - not registered in ParameterTypes since it isn't a
   * general-purpose report parameter.
   */
  export class DimensionRefParameterControl extends Solovis.UI.BaseControl {

    private _portalServices: Solovis.Portal.IPortalServices;
    private _settings: any;

    private initialParameterValue: DimensionRefValue;
    private currentValue: DimensionRefValue;

    private _dimensionTypeSelect: JQuery;
    private _valueControlHost: Solovis.UI.ControlHost;
    private _valueControl;

    constructor(options, info) {
      super(options, info);

      this._portalServices = options.portalServices;
      this._settings = options.settings ? options.settings : {};

      this.initialParameterValue = (typeof options.initialValue != "undefined") ? options.initialValue : null;
      this.currentValue = this.initialParameterValue;
    }

    public static preload(newValue, callback, portalServices: Solovis.Portal.IPortalServices) {
      if (!newValue) {
        callback(null, true);
        return;
      }

      if (newValue.managerResourceId != null) {
        ManagerParameterControl.preload({ resourceId: newValue.managerResourceId }, function (managerData, success) {
          callback(success ? { dimensionType: "manager", manager: managerData } : null, success);
        }, portalServices);
      }
      else {
        TagParameterControl.preload(newValue, function (tagData, success) {
          callback(success ? { dimensionType: "tag", tag: tagData || { tagSetId: newValue.tagSetId } } : null, success);
        }, portalServices);
      }
    }

    public static serializeParameterValue(value: DimensionRefValue): any {
      if (value && value.dimensionType === "manager" && value.manager) {
        return { managerResourceId: value.manager.resourceId };
      }
      else if (value && value.dimensionType === "tag" && value.tag) {
        return TagParameterControl.serializeParameterValue(value.tag);
      }

      return null;
    }

    public static getLabel(value: DimensionRefValue): string {
      if (!value) {
        return "";
      }

      if (value.dimensionType === "manager") {
        return ManagerParameterControl.getLabel(value.manager) + " (Manager)";
      }

      return TagParameterControl.getLabel(value.tag);
    }

    public doRender() {
      const self = this;

      const rowDiv = $("<div>")
        .css({ display: "flex", alignItems: "center", gap: "8px" })
        .appendTo(this.container);

      const dimensionType: "tag" | "manager" = this.currentValue ? this.currentValue.dimensionType : "tag";

      this._dimensionTypeSelect = $("<select>").appendTo(rowDiv);
      $("<option>").attr("value", "tag").text("Tag Set").appendTo(this._dimensionTypeSelect);
      $("<option>").attr("value", "manager").text("Manager").appendTo(this._dimensionTypeSelect);
      this._dimensionTypeSelect.val(dimensionType);

      const valueHostDiv = $("<div>").appendTo(rowDiv);
      this._valueControlHost = new Solovis.UI.ControlHost({ container: valueHostDiv });

      this._renderValueControl(dimensionType, this.currentValue);

      this._dimensionTypeSelect.change(function () {
        const newDimensionType = self._dimensionTypeSelect.val() as "tag" | "manager";
        self.currentValue = null;
        self._renderValueControl(newDimensionType, null);
        self.notifyParameterValueChanged();
      });
    }

    private _renderValueControl(dimensionType: "tag" | "manager", value: DimensionRefValue) {
      const self = this;

      if (dimensionType === "manager") {
        this._valueControl = new ManagerParameterControl({
          controlHost: this._valueControlHost,
          initialValue: value ? value.manager : null,
          portalServices: this._portalServices,
          renderMode: this.options.renderMode
        });
      }
      else {
        this._valueControl = new TagParameterControl({
          controlHost: this._valueControlHost,
          initialValue: value ? value.tag : null,
          portalServices: this._portalServices,
          settings: this._settings,
          renderMode: this.options.renderMode
        }, this.info);
      }

      this._valueControlHost.setCurrentControl(this._valueControl);

      $(this._valueControl).bind("parameterValueChanged", function () {
        const subValue = self._valueControl.getParameterValue();

        if (subValue) {
          const newValue: DimensionRefValue = { dimensionType: dimensionType };
          if (dimensionType === "manager") {
            newValue.manager = subValue;
          }
          else {
            newValue.tag = subValue;
          }

          self.currentValue = newValue;
        }
        else {
          self.currentValue = null;
        }

        self.notifyParameterValueChanged();
      });
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, DimensionRefParameterControl, this._setCurrentValue, newValue, more, this._portalServices);
    }

    public _setCurrentValue(newValue: DimensionRefValue) {
      this.currentValue = newValue;

      if (this._valueControlHost) {
        const dimensionType: "tag" | "manager" = newValue ? newValue.dimensionType : "tag";
        if (this._dimensionTypeSelect) {
          this._dimensionTypeSelect.val(dimensionType);
        }

        this._renderValueControl(dimensionType, newValue);
      }
    }

    public getParameterValue() {
      return this.currentValue ? Solovis.deepCopy(this.currentValue) : null;
    }

    public notifyParameterValueChanged() {
      $(this).trigger("parameterValueChanged");
    }
  }
}
